import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, HostListener, inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { ArticlesService } from '../../../services/articles.service';
import { ActivatedRoute, Router } from '@angular/router';

import { Connect } from '../../../classes/connect';
import { ApiResponse } from '../../../interfaces/api-response';
import { Article } from '../../../interfaces/article';
import { Filters } from '../../../interfaces/filters';
import { FilterComponent } from '../components/filter/filter.component';
import { CardArchiveComponent } from '../components/card-archive/card-archive.component';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ArchiveSearchStateService } from '../../../services/archive-search-state.service';

@Component({
    selector: 'app-archive-historical',
    imports: [
    NgTemplateOutlet,
    FilterComponent,
    CardArchiveComponent,
    MatSidenavModule
],
    templateUrl: './archive-historical.component.html',
    styleUrl: './archive-historical.component.scss'
})
export class ArchiveHistoricalComponent implements OnDestroy {

  mode: 'side' | 'over' | 'push' = 'side';
  isTogglerButton = false;
  isOpenedFilter = true;
  isLogged: boolean = true;
  isSmallScreen: boolean = false;

  @ViewChild('drawer') drawer!: MatDrawer;

  readonly pageSizeOptions = [1,12, 20, 40, 60];

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSize();
  }

  urlServerLaraApiMultimedia = Connect.urlServerLaraApiMultimedia;

  totalResults: number = 0;
  totalPages: number = 1;
  total: number = 0;
  itemsPerPage: number = 20;
  currentPageIndex: number = 1;
  isSmall = false;
  searchText: string | null = null;
  restoredFormValues: Filters | null = null;
  restoredYearFormat: string | null = null;

  filters!: Filters | string | null | undefined;
  type_filter = 0 // Ricerca testuale 0, ricerca non testuale != 0

  articles: Article[] = [];

  formControl = new FormControl('');
  options: string[] = [];
  filteredOptions: Observable<string[]> = new Observable;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly archiveSearchState = inject(ArchiveSearchStateService);
  private pagerObserver?: ResizeObserver;
  private compactPager = false;

  constructor(public articlesService: ArticlesService, private router: Router, private route: ActivatedRoute) { }

  navigate(id: number) {
    this.persistState();
    this.router.navigate(['scheda', id], { relativeTo: this.route })
  }

  ngOnInit() {
    const textFromQuery = this.route.snapshot.queryParamMap.get('text');
    const saved = this.archiveSearchState.getHistorical();

    if (textFromQuery && textFromQuery !== '') {
      this.searchText = textFromQuery;
      this.filters = textFromQuery;
      this.type_filter = 0;
      this.currentPageIndex = 1;
      this.restoredFormValues = this.buildTextFormValues(textFromQuery);
      this.persistState(this.restoredFormValues, null);
      this.clearQueryParams();
      this.fetchArticles(1);
    } else if (saved) {
      this.type_filter = saved.typeFilter;
      this.filters = saved.filters;
      this.itemsPerPage = saved.itemsPerPage;
      this.currentPageIndex = saved.currentPageIndex;
      this.restoredFormValues = saved.formValues;
      this.restoredYearFormat = saved.yearFormat;
      this.searchText = typeof saved.filters === 'string' ? saved.filters : saved.formValues?.title ?? null;
      this.fetchArticles(saved.currentPageIndex);
    } else {
      this.listaArticles();
    }

    if(window.innerWidth < 992) {
      this.isOpenedFilter = false;
    }
    this.checkScreenSize();
  }

  ngAfterViewInit(): void {
    const nav = this.host.nativeElement.querySelector('.archive-pages');
    if (!(nav instanceof HTMLElement)) {
      return;
    }
    this.pagerObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      const compact = width < 640;
      if (compact === this.compactPager) {
        return;
      }
      this.zone.run(() => {
        this.compactPager = compact;
        this.scrollActivePageIntoView();
      });
    });
    this.pagerObserver.observe(nav);
  }

  ngOnDestroy(): void {
    this.pagerObserver?.disconnect();
  }

  private checkScreenSize() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 992 && this.mode == 'side') {
      this.mode = 'over';
      if (this.drawer && this.drawer.opened) {
        this.drawer.toggle();
      }
    }
    else if (screenWidth >= 992 && this.mode == 'over') {
      this.mode = 'side';
      if (this.drawer && !this.drawer.opened) {
        this.drawer.toggle();
      }
    }

    if(this.mode == 'side') {
      this.isTogglerButton = false;
    }
    else {
      this.isTogglerButton = true;
    }

    const screenHeight = window.innerHeight;
    if(screenHeight < 1000) {
      this.isSmallScreen = true;
    }
    else {
      this.isSmallScreen = false;
    }
  }

  scrollToTopOfPage() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private listaArticles() {
    this.fetchArticles(this.currentPageIndex);
  }

  searchArticles(obj: {
    type: number;
    obj_filter: Filters | string | null | undefined;
    formValues: Filters;
    yearFormat: string | null;
  }) {
    this.filters = obj.obj_filter;
    this.type_filter = obj.type;
    this.currentPageIndex = 1;
    this.restoredFormValues = obj.formValues;
    this.restoredYearFormat = obj.yearFormat;
    this.searchText = obj.type === 0 && typeof obj.obj_filter === 'string'
      ? obj.obj_filter
      : null;
    this.persistState(obj.formValues, obj.yearFormat);
    this.fetchArticles(1);
    this.closeFilterPanel();
  }

  get activeFilterLabels(): string[] {
    const filters = this.restoredFormValues;
    if (!filters) {
      return [];
    }

    // Ricerca testuale e filtri strutturali sono mutuamente esclusivi
    if (this.type_filter === 0) {
      const title = filters.title?.trim();
      return title ? [`ricerca: '${title}'`] : [];
    }

    const labels: string[] = [];

    filters.categories?.forEach((category) => {
      if (category.title) {
        labels.push(category.title);
      }
    });

    if (filters.city) {
      const cityName = typeof filters.city === 'string' ? filters.city.trim() : filters.city.name?.trim();
      if (cityName) {
        labels.push(cityName);
      }
    }

    const point = filters.pointofinterest?.trim();
    if (point) {
      labels.push(point);
    }

    if (filters.date_type === 0 && filters.date_article) {
      const formatted = this.formatFilterDate(filters.date_article);
      if (formatted) {
        labels.push(formatted);
      }
    } else if (filters.date_type === 1 && filters.year_article?.trim()) {
      labels.push(filters.year_article.trim());
    } else if (filters.date_type === 2) {
      const from = filters.yearFrom_article?.trim();
      const to = filters.yearTo_article?.trim();
      if (from && to) {
        labels.push(`${from}–${to}`);
      } else if (from) {
        labels.push(`Dal ${from}`);
      } else if (to) {
        labels.push(`Al ${to}`);
      }
    }

    return labels;
  }

  private formatFilterDate(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleDateString('it-IT');
  }

  private closeFilterPanel(): void {
    if (this.mode === 'over' && this.drawer) {
      void this.drawer.close();
    }
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPageIndex) {
      return;
    }
    this.currentPageIndex = page;
    this.persistState();
    this.fetchArticles(page);
    this.scrollToTopOfPage();
  }

  setItemsPerPage(event: Event) {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!this.pageSizeOptions.includes(value) || value === this.itemsPerPage) {
      return;
    }
    this.itemsPerPage = value;
    this.currentPageIndex = 1;
    this.persistState();
    this.fetchArticles(1);
    this.scrollToTopOfPage();
  }

  get pageTokens(): Array<number | 'ellipsis'> {
    const total = Math.max(this.totalPages, 1);
    const current = this.currentPageIndex;
    if (this.compactPager) {
      const start = Math.max(1, current - 1);
      const end = Math.min(total, current + 1);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }
    const tokens: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) {
      tokens.push('ellipsis');
    }
    for (let page = start; page <= end; page++) {
      tokens.push(page);
    }
    if (end < total - 1) {
      tokens.push('ellipsis');
    }
    tokens.push(total);
    return tokens;
  }

  private fetchArticles(page: number) {
    this.articlesService.getArticlesFromServer(this.itemsPerPage, page, this.type_filter, this.filters).
      subscribe((val: ApiResponse<{ listArticles: Article[], totalPages: number, totalResults: number }>) => {
        this.articles = val.data.listArticles;
        this.totalPages = val.data.totalPages;
        this.totalResults = val.data.totalResults;
        this.currentPageIndex = page;
        if (this.totalPages == 0) {
          this.totalPages = 1;
        }
        this.persistState();
        this.scrollActivePageIntoView();
      });
  }

  private persistState(formValues = this.restoredFormValues, yearFormat = this.restoredYearFormat): void {
    this.archiveSearchState.setHistorical({
      typeFilter: this.type_filter,
      filters: this.filters ?? null,
      formValues: formValues ?? null,
      yearFormat: yearFormat ?? null,
      itemsPerPage: this.itemsPerPage,
      currentPageIndex: this.currentPageIndex,
    });
  }

  private buildTextFormValues(title: string): Filters {
    return {
      title,
      categories: null,
      city: null,
      pointofinterest: null,
      date_type: null,
      date_article: null,
      yearFrom_article: null,
      yearTo_article: null,
      year_article: null,
    };
  }

  private clearQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  onPageListScroll(event: Event): void {
    const list = event.target;
    if (list instanceof HTMLElement) {
      this.updatePageListFade(list);
    }
  }

  private scrollActivePageIntoView(): void {
    setTimeout(() => {
      this.host.nativeElement.querySelectorAll('.page-list').forEach((list: Element) => {
        if (!(list instanceof HTMLElement)) {
          return;
        }
        const active = list.querySelector('.is-active');
        if (active instanceof HTMLElement) {
          const left = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
          list.scrollTo({ left: Math.max(0, left) });
        }
        this.updatePageListFade(list);
      });
    });
  }

  private updatePageListFade(list: HTMLElement): void {
    const maxScroll = list.scrollWidth - list.clientWidth;
    const canScrollLeft = maxScroll > 1 && list.scrollLeft > 1;
    const canScrollRight = maxScroll > 1 && list.scrollLeft < maxScroll - 1;
    list.classList.toggle('can-scroll-left', canScrollLeft);
    list.classList.toggle('can-scroll-right', canScrollRight);
  }

}
